import React from 'react';
import { Search, RotateCcw } from 'lucide-react';
import { Manufacturer, VehicleType } from '@/types';
import { UseVehicleModelFiltersReturn } from '@/hooks/useVehicleModelFilters';
import {
  POWERTRAIN_OPTIONS,
  VEHICLE_TAG_OPTIONS,
  FORM_LABELS,
  BUTTON_TEXTS,
} from '@/constants/vehicleModelConstants';

interface Props {
  manufacturers: Manufacturer[];
  vehicleTypes: VehicleType[];
  filterHooks: UseVehicleModelFiltersReturn;
}

export const VehicleModelFiltersComponent: React.FC<Props> = ({
  manufacturers,
  vehicleTypes,
  filterHooks,
}) => {
  const { filters, updateFilter, resetFilters, hasActiveFilters } = filterHooks;

  const handleTagChange = (tag: string, checked: boolean) => {
    const currentTags = filters.selectedTags;
    if (checked) {
      updateFilter('selectedTags', [...currentTags, tag as any]);
    } else {
      updateFilter(
        'selectedTags',
        currentTags.filter(t => t !== tag)
      );
    }
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6'>
      <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 lg:mb-0'>
          車両モデル検索・フィルター
        </h3>

        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className='flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors'
          >
            <RotateCcw className='w-4 h-4' />
            {BUTTON_TEXTS.RESET_FILTERS}
          </button>
        )}
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4'>
        {/* テキスト検索 */}
        <div className='sm:col-span-2 lg:col-span-3 xl:col-span-2'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            テキスト検索
          </label>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              value={filters.searchQuery}
              onChange={e => updateFilter('searchQuery', e.target.value)}
              placeholder={FORM_LABELS.SEARCH_PLACEHOLDER}
              className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            />
          </div>
        </div>

        {/* メーカーフィルター */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            {FORM_LABELS.MANUFACTURER_FILTER}
            {filters.selectedManufacturer && (
              <span className='ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'>
                1
              </span>
            )}
          </label>
          <select
            value={filters.selectedManufacturer}
            onChange={e => updateFilter('selectedManufacturer', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              filters.selectedManufacturer
                ? 'border-blue-300 dark:border-blue-600'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            <option value=''>全メーカー</option>
            {manufacturers.map(manufacturer => (
              <option key={manufacturer.id} value={manufacturer.id}>
                {manufacturer.name}
              </option>
            ))}
          </select>
        </div>

        {/* 車種タイプフィルター */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            {FORM_LABELS.VEHICLE_TYPE_FILTER}
            {filters.selectedVehicleType && (
              <span className='ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'>
                1
              </span>
            )}
          </label>
          <select
            value={filters.selectedVehicleType}
            onChange={e => updateFilter('selectedVehicleType', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              filters.selectedVehicleType
                ? 'border-blue-300 dark:border-blue-600'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            <option value=''>全車種</option>
            {vehicleTypes.map(vehicleType => (
              <option key={vehicleType.id} value={vehicleType.id}>
                {vehicleType.name}
              </option>
            ))}
          </select>
        </div>

        {/* パワートレインフィルター */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            {FORM_LABELS.POWERTRAIN_FILTER}
            {filters.selectedPowertrain && (
              <span className='ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'>
                1
              </span>
            )}
          </label>
          <select
            value={filters.selectedPowertrain}
            onChange={e => updateFilter('selectedPowertrain', e.target.value as any)}
            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              filters.selectedPowertrain
                ? 'border-blue-300 dark:border-blue-600'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            <option value=''>全パワートレイン</option>
            {POWERTRAIN_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* タグフィルター */}
      <div className='mt-4'>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
          {FORM_LABELS.TAGS_FILTER}
          {filters.selectedTags.length > 0 && (
            <span className='ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'>
              {filters.selectedTags.length}
            </span>
          )}
        </label>
        <div className='flex flex-wrap gap-2'>
          {VEHICLE_TAG_OPTIONS.map(option => (
            <label
              key={option.value}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                filters.selectedTags.includes(option.value)
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                  : 'bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              <input
                type='checkbox'
                checked={filters.selectedTags.includes(option.value)}
                onChange={e => handleTagChange(option.value, e.target.checked)}
                className='rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500'
              />
              <span className='text-sm text-gray-700 dark:text-gray-300'>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
